import { isValidElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { TOOL_STATUS_DOT } from '../../toolPageUtils';

/** Accepts either a rendered element or an icon component. */
function renderActionIcon(icon) {
    if (!icon) return null;
    if (isValidElement(icon)) return icon;

    const Icon = icon;
    return <Icon size={18} stroke={1.9} />;
}

/**
 * Publish the height of the sticky action bar as `--tool-action-bar-h` on the
 * document, so the app's floating buttons can sit above it instead of over the
 * tool's own controls.
 *
 * The strip is the page's most important row — it holds the run button — and it
 * is pinned to the same corner the WhatsApp button and the support floats live
 * in, so without this the floats cover the very button the page exists for. The
 * measurement includes the bar's own bottom inset, which is the mobile nav's
 * 76 px below the `nav` breakpoint and 0 above it.
 */
function useStickyBarLift(ref, sticky) {
    useLayoutEffect(() => {
        const el = ref.current;
        const root = document.documentElement;
        if (!sticky || !el) return undefined;

        const measure = () => {
            const inset = parseFloat(getComputedStyle(el).bottom) || 0;
            root.style.setProperty('--tool-action-bar-h', `${Math.round(el.offsetHeight + inset)}px`);
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
            root.style.removeProperty('--tool-action-bar-h');
        };
    }, [ref, sticky]);

    // A tool that drops the bar mid-session (it is only rendered once there is
    // something to act on) must not leave the floats parked halfway up.
    useEffect(() => () => document.documentElement.style.removeProperty('--tool-action-bar-h'), []);
}

/** True at the desktop layout (≥1024px), where the bar is allowed to pin. */
function useIsDesktop() {
    const query = '(min-width: 1024px)';
    const [matches, setMatches] = useState(() => (
        typeof window !== 'undefined' && window.matchMedia(query).matches
    ));
    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    return matches;
}

/**
 * Whether a sticky bar is currently pinned to the screen or resting in its own
 * spot in the page. A zero-height marker sits right after the bar: while that
 * spot is still below the fold the bar is pinned; once it has scrolled into view
 * the bar is simply part of the page.
 */
function useIsPinned(markerRef, enabled) {
    const [pinned, setPinned] = useState(true);
    useEffect(() => {
        if (!enabled) return undefined;
        let frame = 0;
        const check = () => {
            frame = 0;
            const marker = markerRef.current;
            if (!marker) return;
            setPinned(marker.getBoundingClientRect().top > window.innerHeight);
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(check); };
        check();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };
    }, [markerRef, enabled]);
    return enabled && pinned;
}

export default function ToolActionBar({
    status = 'empty',
    statusText,
    onReset,
    resetLabel,
    primaryLabel,
    onPrimary,
    primaryDisabled = false,
    primaryLoading = false,
    primaryIcon,
    /**
     * Optional outlined action(s) shown just before the primary button — one
     * object, or an array when a finished run offers more than one follow-up
     * (print, go back a step…). Falsy entries are skipped.
     */
    secondaryAction,
    /**
     * A control that belongs WITH the run button rather than in the settings
     * column — an output-format picker, say. Sits first in the button row, so the
     * choice is read before the button it changes.
     */
    actionExtra,
    /**
     * Pin the bar to the bottom of the screen (the design system's "sticky action
     * bar") on the desktop layout. It rides the viewport bottom while the tool is
     * in view and settles, as a card the width of the tool, where the page puts it.
     */
    sticky = false,
}) {
    const { t } = useTranslation();
    const barRef = useRef(null);
    const markerRef = useRef(null);
    // Pinning only happens on the desktop layout. Below 1024px the screen is too
    // short and already carries the fixed bottom nav, so a pinned strip sat on top
    // of the settings being edited — there the bar is an ordinary card in the page.
    const isDesktop = useIsDesktop();
    const canPin = sticky && isDesktop;
    const pinned = useIsPinned(markerRef, canPin);
    useStickyBarLift(barRef, pinned);

    // The bar itself never changes size or position between its two looks — only
    // its skin does. Swapping margins and padding when it un-pinned made the
    // buttons jump sideways; instead two painted layers sit behind a row that
    // stays put, and they cross-fade:
    //
    //   • pinned  — a full-bleed strip (translucent, blurred, hairline, soft
    //     upward shadow) spanning the screen.
    //   • resting — a card the width of the tool column, like the panels above.
    //
    // The full-bleed layer breaks out with `left-1/2 w-screen -translate-x-1/2`;
    // that is safe here because it is an absolute child, not the sticky element
    // (on a sticky element `left` would be a sticky inset).
    const outerClass = canPin
        ? 'sticky bottom-0 z-30 mt-6'
        : 'relative mt-4';
    const rowClass = canPin
        ? 'relative flex flex-wrap items-center gap-3.5 px-4 py-[13px] sm:px-5'
        : 'relative flex flex-wrap items-center gap-3.5 px-4 py-3 sm:px-5';
    const fade = 'pointer-events-none absolute transition-opacity duration-300 ease-out';

    return (
        <>
        <div ref={barRef} className={outerClass}>
        {canPin ? (
            <div
                aria-hidden="true"
                className={`${fade} inset-y-0 left-1/2 w-screen -translate-x-1/2 border-t border-[#ECE8E4] bg-white/[0.92] shadow-[0_-6px_20px_rgba(28,24,21,0.05)] backdrop-blur-[8px] dark:border-zinc-700 dark:bg-zinc-900/90 ${
                    pinned ? 'opacity-100' : 'opacity-0'
                }`}
            />
        ) : null}
        <div
            aria-hidden="true"
            className={`${fade} inset-0 rounded-xl border border-[#ECE8E4] bg-white shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900 ${
                pinned ? 'opacity-0' : 'opacity-100'
            }`}
        />
        <div className={rowClass}>
            <div className="flex min-w-[140px] flex-1 items-center gap-2 text-[13px] text-[#6B635C] dark:text-zinc-400">
                <span className={`h-2 w-2 flex-none rounded-full ${TOOL_STATUS_DOT[status] || TOOL_STATUS_DOT.empty}`} />
                <span>{statusText}</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={onReset}
                    disabled={!onReset}
                    className="h-[46px] rounded-xl border border-[#ECE8E4] bg-white px-[18px] text-[14.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                >
                    {resetLabel || t('toolPage.reset')}
                </button>

                {/* Between Reset and the follow-up buttons: it belongs to the run
                    button, and Reset stays where it has always been. */}
                {actionExtra}

                {[].concat(secondaryAction || []).filter(Boolean).map((action) => (
                    <button
                        key={action.key || action.label}
                        type="button"
                        onClick={action.onClick}
                        disabled={action.disabled}
                        className="inline-flex h-[46px] items-center gap-2 rounded-xl border border-[#ECE8E4] bg-white px-[18px] text-[14.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                        {renderActionIcon(action.icon)}
                        {action.label}
                    </button>
                ))}

                <button
                    type="button"
                    onClick={onPrimary}
                    disabled={primaryDisabled || primaryLoading}
                    className={`inline-flex h-[46px] items-center gap-2 rounded-xl px-[22px] text-[14.5px] font-semibold transition-colors ${
                        primaryDisabled || primaryLoading
                            ? 'cursor-not-allowed bg-[#F7F5F3] text-[#A8A099] dark:bg-zinc-800 dark:text-zinc-600'
                            : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                >
                    {primaryLoading ? (
                        <IconLoader2 size={18} className="animate-spin" />
                    ) : (
                        renderActionIcon(primaryIcon)
                    )}
                    {primaryLabel}
                </button>
            </div>
        </div>
        </div>
        {/* Where the bar sits in the page — see useIsPinned. */}
        {canPin ? <div ref={markerRef} aria-hidden="true" className="h-0" /> : null}
        </>
    );
}
